import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { Col, FormCheck, Row, Spinner } from 'react-bootstrap'
import { FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useDispatch, useSelector } from 'react-redux'
import Button from '../../../components/button'
import CustomModal from '../../../components/CustomModal'
import Input from '../../../components/input'
import { fetchPermissionData, getSinglePermissionGroup, permissionFetchModule, updatePermissionGroup } from '../../../store/Slices/PermissionSlice'
import '../../../styles/PermissionGroup.css'
import { permissionValidationSchema } from '../../../utils/Schema'
import { searchIcon } from '../../../utils/svgs'
import { errorNotify, successNotify } from '../../../utils/toast'

const EditPermissionGroup = ({ show, onHide, currentPage, limit, permissionGroupId, searchFilters }) => {
    const dispatch = useDispatch();

    const {
        fetchModules = [],
        fetchModulesLoading,
        updateLoading,
        singlePermissionGroup,
        singlePermissionLoading
    } = useSelector((state) => state.permissions);

    const [showSelected, setShowSelected] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [expandedModules, setExpandedModules] = useState({});
    const [expandedMenus, setExpandedMenus] = useState({});
    const [activeMenu, setActiveMenu] = useState(null);
    const [checkedMenus, setCheckedMenus] = useState({});
    const [checkedModules, setCheckedModules] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (show && permissionGroupId) {
            dispatch(permissionFetchModule());
            dispatch(getSinglePermissionGroup(permissionGroupId));
        }
    }, [show, permissionGroupId])

    // Set default values when singlePermissionGroup data is loaded
    useEffect(() => {
        if (singlePermissionGroup && fetchModules.length > 0) {
            const apiData = singlePermissionGroup?.data;

            const initialSelectedPermissions = [];
            const initialCheckedModules = {};
            const initialCheckedMenus = {};
            const initialExpandedModules = {};

            apiData.forEach(module => {
                // Check if module should be checked
                if (module.has_module_permission) {
                    initialCheckedModules[module.id] = true;
                    initialExpandedModules[module.id] = true; // Auto expand checked modules
                }

                // Check menus and permissions
                module.menus?.forEach(menu => {
                    if (menu.has_permissions) {
                        initialCheckedMenus[menu.id] = true;
                    }

                    // Check individual permissions
                    menu.permissions?.forEach(permission => {
                        if (permission.selected_permission) {
                            initialSelectedPermissions.push({
                                module_id: permission.module_id,
                                modules_menu_id: permission.modules_menu_id,
                                permission_id: permission.id
                            });
                        }
                    });
                });
            });

            setSelectedPermissions(initialSelectedPermissions);
            setCheckedModules(initialCheckedModules);
            setCheckedMenus(initialCheckedMenus);
            setExpandedModules(initialExpandedModules);
        }
    }, [singlePermissionGroup, fetchModules]);

    const resetAllStates = () => {
        setShowSelected(false);
        setSelectedPermissions([]);
        setExpandedModules({});
        setExpandedMenus({});
        setActiveMenu(null);
        setCheckedMenus({});
        setCheckedModules({});
        setSearchTerm('');
    };

    const handleModalClose = () => {
        resetAllStates();
        onHide();
    };

    const handleModuleCheckboxChange = (moduleId, module, event) => {
        event.stopPropagation();

        const isCurrentlyChecked = checkedModules[moduleId];

        setCheckedModules(prev => ({
            ...prev,
            [moduleId]: !isCurrentlyChecked
        }));

        if (!isCurrentlyChecked) {
            // Checking module - check all its menus and their permissions
            const moduleMenuIds = module.menus?.map(menu => menu.id) || [];
            setCheckedMenus(prev => {
                const updated = { ...prev };
                moduleMenuIds.forEach(menuId => {
                    updated[menuId] = true;
                });
                return updated;
            });

            // Auto-select all permissions for all menus in this module
            module.menus?.forEach(menu => {
                const menuPermissions = menu.permissions || [];
                menuPermissions.forEach(permission => {
                    const permissionData = {
                        module_id: permission.module_id,
                        modules_menu_id: permission.modules_menu_id,
                        permission_id: permission.id
                    };

                    setSelectedPermissions(prev => {
                        const existingIndex = prev.findIndex(
                            p => p.module_id === permissionData.module_id &&
                                p.modules_menu_id === permissionData.modules_menu_id &&
                                p.permission_id === permissionData.permission_id
                        );

                        if (existingIndex === -1) {
                            return [...prev, permissionData];
                        }
                        return prev;
                    });
                });
            });
        } else {
            // Unchecking module - uncheck all its menus and their permissions
            const moduleMenuIds = module.menus?.map(menu => menu.id) || [];
            setCheckedMenus(prev => {
                const updated = { ...prev };
                moduleMenuIds.forEach(menuId => {
                    delete updated[menuId];
                });
                return updated;
            });

            // Remove all permissions for all menus in this module
            module.menus?.forEach(menu => {
                const menuPermissions = menu.permissions || [];
                menuPermissions.forEach(permission => {
                    setSelectedPermissions(prev => prev.filter(
                        p => !(p.module_id === permission.module_id &&
                            p.modules_menu_id === permission.modules_menu_id &&
                            p.permission_id === permission.id)
                    ));
                });
            });

            if (activeMenu && module.menus?.some(menu => menu.id === activeMenu.id)) {
                setActiveMenu(null);
            }
        }
    };

    const handleMenuCheckboxChange = (menuId, menu, event) => {
        event.stopPropagation();

        const isCurrentlyChecked = checkedMenus[menuId];

        setCheckedMenus(prev => ({
            ...prev,
            [menuId]: !isCurrentlyChecked
        }));

        // Find the parent module for this menu
        const parentModule = fetchModules.find(module =>
            module.menus?.some(m => m.id === menuId)
        );

        // Auto-select or deselect all permissions for this menu
        if (!isCurrentlyChecked) {
            // Checking menu - auto-check parent module and all permissions for this menu
            if (parentModule) {
                setCheckedModules(prev => ({
                    ...prev,
                    [parentModule.id]: true
                }));
            }

            // Check all permissions for this menu
            const menuPermissions = menu.permissions || [];
            menuPermissions.forEach(permission => {
                const permissionData = {
                    module_id: permission.module_id,
                    modules_menu_id: permission.modules_menu_id,
                    permission_id: permission.id
                };

                setSelectedPermissions(prev => {
                    const existingIndex = prev.findIndex(
                        p => p.module_id === permissionData.module_id &&
                            p.modules_menu_id === permissionData.modules_menu_id &&
                            p.permission_id === permissionData.permission_id
                    );

                    if (existingIndex === -1) {
                        return [...prev, permissionData];
                    }
                    return prev;
                });
            });
            setActiveMenu(menu);
        } else {
            // Uncheck all permissions for this menu
            const menuPermissions = menu.permissions || [];
            menuPermissions.forEach(permission => {
                setSelectedPermissions(prev => prev.filter(
                    p => !(p.module_id === permission.module_id &&
                        p.modules_menu_id === permission.modules_menu_id &&
                        p.permission_id === permission.id)
                ));
            });

            // Check if this was the last checked menu in the parent module
            if (parentModule) {
                const otherCheckedMenus = parentModule.menus?.filter(m =>
                    m.id !== menuId && checkedMenus[m.id]
                ) || [];

                if (otherCheckedMenus.length === 0) {
                    setCheckedModules(prev => ({
                        ...prev,
                        [parentModule.id]: false
                    }));
                }
            }

            if (activeMenu?.id === menu.id) {
                setActiveMenu(null);
            }
        }
    };

    const handlePermissionChange = (permission) => {
        const permissionData = {
            module_id: permission.module_id,
            modules_menu_id: permission.modules_menu_id,
            permission_id: permission.id
        };

        setSelectedPermissions(prev => {
            const existingIndex = prev.findIndex(
                p => p.module_id === permissionData.module_id &&
                    p.modules_menu_id === permissionData.modules_menu_id &&
                    p.permission_id === permissionData.permission_id
            );

            if (existingIndex !== -1) {
                // Removing permission - check if this is the last permission for this menu
                const updatedPermissions = prev.filter((_, index) => index !== existingIndex);

                // Check if there are any other permissions for this menu
                const hasOtherPermissions = updatedPermissions.some(
                    p => p.modules_menu_id === permission.modules_menu_id
                );

                // If no other permissions for this menu, uncheck the menu and possibly the module
                if (!hasOtherPermissions) {
                    setCheckedMenus(prevMenus => ({
                        ...prevMenus,
                        [permission.modules_menu_id]: false
                    }));

                    // Check if we need to uncheck the parent module
                    const parentModule = fetchModules.find(module =>
                        module.menus?.some(m => m.id === permission.modules_menu_id)
                    );

                    if (parentModule) {
                        const otherCheckedMenus = parentModule.menus?.filter(m =>
                            m.id !== permission.modules_menu_id && checkedMenus[m.id]
                        ) || [];

                        if (otherCheckedMenus.length === 0) {
                            setCheckedModules(prevModules => ({
                                ...prevModules,
                                [parentModule.id]: false
                            }));
                        }
                    }
                }

                return updatedPermissions;
            } else {
                // Adding permission - auto-check the parent menu and module
                setCheckedMenus(prevMenus => ({
                    ...prevMenus,
                    [permission.modules_menu_id]: true
                }));

                // Auto-check the parent module
                const parentModule = fetchModules.find(module =>
                    module.menus?.some(m => m.id === permission.modules_menu_id)
                );

                if (parentModule) {
                    setCheckedModules(prevModules => ({
                        ...prevModules,
                        [parentModule.id]: true
                    }));
                }

                return [...prev, permissionData];
            }
        });
    };

    const handleExpendedMenuCheckbox = (menuId, menu, event) => {
        event.stopPropagation();
        // Only show permissions, don't auto-select
        setActiveMenu(menu);
    };

    const isPermissionSelected = (permission) => {
        return selectedPermissions.some(
            p => p.module_id === permission.module_id &&
                p.modules_menu_id === permission.modules_menu_id &&
                p.permission_id === permission.id
        );
    };

    const getSelectedPermissionsForDisplay = () => {
        if (!activeMenu) return [];
        return activeMenu.permissions || [];
    };

    const filteredPermissions = showSelected
        ? getSelectedPermissionsForDisplay().filter(permission => isPermissionSelected(permission))
        : getSelectedPermissionsForDisplay();

    const handleModuleExpansion = (moduleId) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }));
    };

    const isModuleSelected = (module) => {
        return module.menus?.some(menu => checkedMenus[menu.id]);
    };

    const isMenuSelected = (menuId) => {
        return checkedMenus[menuId];
    };

    const getFilteredModules = () => {
        let modules = fetchModules.filter(module =>
            module.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (showSelected) {
            modules = modules.filter(module => isModuleSelected(module));
        }

        // Check if current active menu's module is still visible
        if (activeMenu) {
            const activeMenuModuleVisible = modules.some(module =>
                module.menus?.some(menu => menu.id === activeMenu.id)
            );

            if (!activeMenuModuleVisible) {
                setActiveMenu(null);
            }
        }

        return modules;
    };

    const getFilteredMenus = (moduleMenus) => {
        if (!moduleMenus) return [];

        if (showSelected) {
            return moduleMenus.filter(menu => isMenuSelected(menu.id));
        }

        return moduleMenus;
    };

    const filteredModules = getFilteredModules();

    const initialValues = {
        name: singlePermissionGroup?.group_permission?.name || '',
        description: singlePermissionGroup?.group_permission?.description || '',
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const userData = {
                group_permissions_id: permissionGroupId,
                name: values.name,
                description: values.description,
                permission_groups: selectedPermissions
            };

            if (userData?.permission_groups.length === 0) {
                errorNotify('Please select at least one permission');
                return;
            }

            await dispatch(updatePermissionGroup(userData)).unwrap();
            successNotify('Permission Group updated successfully');
            resetForm();
            onHide();
            dispatch(fetchPermissionData({
                page: currentPage,
                limit: limit,
                ...(searchFilters && { searchFilters: searchFilters })
            }));

        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <CustomModal heading="Edit Permission Group" size="lg" show={show} handleClose={onHide}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={permissionValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                        <>
                            {
                                singlePermissionLoading ? <div className='loading_spinner' style={{ minHeight: "70vh" }}><Spinner animation="grow" /></div> :
                                    <Form>
                                        <div className='add_user_modal'>
                                            <h5>User Information</h5>

                                            <div>
                                                <Input
                                                    label="Name"
                                                    placeholder="Enter Permission Group Name"
                                                    type="text"
                                                    name="name"
                                                    value={values.name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.name}
                                                    touched={touched.name}
                                                />
                                                <Input
                                                    label="Description"
                                                    placeholder="Enter Permission Group Description"
                                                    type="text"
                                                    name="description"
                                                    value={values.description}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.description}
                                                    touched={touched.description}
                                                />

                                                <hr />
                                            </div>

                                            <h5>Permissions</h5>

                                            <div className='permission_wrapper'>
                                                <div className='permission_header'>
                                                    <div className='table_header_search'>
                                                        <div>
                                                            {searchIcon}
                                                            <input
                                                                type="text"
                                                                placeholder="Search"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='permission_toggle_wrapper'>
                                                        <FormCheck
                                                            type="switch"
                                                            id="show-selected-switch"
                                                            label="Show Selected"
                                                            checked={showSelected}
                                                            onChange={(e) => setShowSelected(e.target.checked)}
                                                            className="custom-toggle-switch"
                                                        />
                                                    </div>
                                                </div>
                                                <div className='permission_group_container'>
                                                    <Row className="permission_content_row">
                                                        <Col md={6} className="modules_column">
                                                            <div className='modules_section'>
                                                                <h6>Modules</h6>

                                                                <div className='modules_list'>
                                                                    {filteredModules.map((module) => (
                                                                        <div key={module.id} className='module_accordion'>
                                                                            <div
                                                                                className={`module_item main_module ${expandedModules[module.id] ? 'expanded' : ''}`}>
                                                                                <span
                                                                                    className="module_arrow"
                                                                                    onClick={() => handleModuleExpansion(module.id)}
                                                                                    style={{ cursor: 'pointer' }}
                                                                                >
                                                                                    {expandedModules[module.id] ? <FiChevronDown /> : <FiChevronRight />}
                                                                                </span>
                                                                                <FormCheck
                                                                                    type="checkbox"
                                                                                    label={module.name}
                                                                                    checked={checkedModules[module.id] || false}
                                                                                    className="custom-permission-checkbox"
                                                                                    onClick={(e) => handleModuleCheckboxChange(module.id, module, e)}
                                                                                />
                                                                            </div>

                                                                            {expandedModules[module.id] && (
                                                                                <div className='menus_list'>
                                                                                    {getFilteredMenus(module.menus).map((menu) => (
                                                                                        <div key={menu.id} className='menu_accordion'>
                                                                                            <div
                                                                                                className={`module_item sub_module ${activeMenu?.id === menu.id ? 'active' : ''} ${expandedMenus[menu.id] ? 'expanded' : ''}`}
                                                                                            >
                                                                                                <FormCheck
                                                                                                    type="checkbox"
                                                                                                    checked={checkedMenus[menu.id] || false}
                                                                                                    onClick={(e) => handleMenuCheckboxChange(menu.id, menu, e)}
                                                                                                    className="custom-permission-checkbox"
                                                                                                />
                                                                                                <span onClick={(e) => handleExpendedMenuCheckbox(menu.id, menu, e)}>{menu.name}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                            </div>
                                                        </Col>
                                                        <Col md={6} className="permissions_column">
                                                            <div className='permissions_section'>
                                                                <h6>Permissions</h6>
                                                                <div className='permissions_list'>
                                                                    {!showSelected ? (
                                                                        <div className='permission_module_group'>
                                                                            {filteredPermissions.map((permission) => (
                                                                                <div key={permission.id} className='permission_item'>
                                                                                    <FormCheck
                                                                                        type="checkbox"
                                                                                        id={`permission-${permission.id}`}
                                                                                        label={permission.name}
                                                                                        checked={isPermissionSelected(permission)}
                                                                                        onChange={() => handlePermissionChange(permission)}
                                                                                        className="custom-permission-checkbox"
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        selectedPermissions.length > 0 && (
                                                                            filteredPermissions.map((permission) => (
                                                                                <div key={permission.id} className='permission_item'>
                                                                                    <FormCheck
                                                                                        type="checkbox"
                                                                                        id={`permission-${permission.id}`}
                                                                                        label={permission.name}
                                                                                        checked={true}
                                                                                        onChange={() => handlePermissionChange(permission)}
                                                                                        className="custom-permission-checkbox"
                                                                                    />
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                            <div style={{ marginTop: "80px" }}>
                                                <Button
                                                    cancelBtn={handleModalClose}
                                                    submitBtn={() => { }}
                                                    loading={updateLoading}
                                                    disabled={updateLoading}
                                                    submitText="Update"
                                                    type="submit"
                                                />
                                            </div>
                                        </div>
                                    </Form>
                            }
                        </>
                    )}
                </Formik>
            </CustomModal>
        </div>
    )
}

export default EditPermissionGroup